import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Quote, Star, ChevronLeft, ChevronRight } from "lucide-react";
import { testimonials } from "@/data/content";

const AUTO_ADVANCE_MS = 2000;

const TestimonialCard = ({ t, i }) => (
    <article
        className="relative h-full rounded-3xl border border-slate-200 bg-white p-8 sm:p-10 shadow-sm"
        data-testid={`testimonial-card-${i}`}
    >
        <div className="absolute -top-4 -left-4 w-14 h-14 rounded-2xl bg-blue-600 text-white flex items-center justify-center shadow-lg">
            <Quote className="w-6 h-6" />
        </div>

        <div className="flex items-center gap-1 mb-5 pt-2">
            {Array.from({ length: 5 }).map((_, s) => (
                <Star key={s} className="w-4 h-4 text-brand-yellow" fill="#FFC300" strokeWidth={0} />
            ))}
        </div>

        <p className="font-display text-lg sm:text-xl font-medium text-slate-800 leading-relaxed">
            &ldquo;{t.quote}&rdquo;
        </p>

        <div className="mt-8 pt-6 border-t border-slate-100 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-center shrink-0 overflow-hidden">
                {t.logo ? (
                    <img src={t.logo} alt={t.company} className="max-h-8 w-auto" loading="lazy" />
                ) : (
                    <span className="font-display font-black text-slate-500 text-xs tracking-tight leading-none text-center px-1">
                        {t.company.split(" ")[0]}
                    </span>
                )}
            </div>
            <div className="min-w-0">
                <div className="font-display font-bold text-slate-900 text-sm leading-tight">{t.author}</div>
                <div className="text-slate-500 text-xs mt-0.5 truncate">{t.company}</div>
            </div>
            {t.result && (
                <div className="ml-auto text-right shrink-0 hidden sm:block">
                    <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-400">Outcome</div>
                    <div className="text-xs font-bold text-brand-yellow mt-0.5">{t.result}</div>
                </div>
            )}
        </div>
    </article>
);

export const TestimonialsSection = () => {
    const [perView, setPerView] = useState(() =>
        typeof window !== "undefined" && window.innerWidth >= 768 ? 2 : 1
    );
    const [page, setPage] = useState(0);
    const [paused, setPaused] = useState(false);
    const pageCount = Math.ceil(testimonials.length / perView);
    const timerRef = useRef(null);

    useEffect(() => {
        const onResize = () => setPerView(window.innerWidth >= 768 ? 2 : 1);
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    useEffect(() => {
        setPage((p) => Math.min(p, Math.ceil(testimonials.length / perView) - 1));
    }, [perView]);

    const goTo = useCallback(
        (p) => setPage(((p % pageCount) + pageCount) % pageCount),
        [pageCount]
    );

    useEffect(() => {
        if (paused) return undefined;
        timerRef.current = setInterval(() => setPage((p) => (p + 1) % pageCount), AUTO_ADVANCE_MS);
        return () => clearInterval(timerRef.current);
    }, [paused, pageCount, page]);

    return (
        <section
            className="relative py-24 sm:py-32 bg-white border-t border-slate-200 overflow-hidden"
            data-testid="testimonials-section"
        >
            <div className="absolute top-1/2 -left-24 w-96 h-96 rounded-full bg-blue-600/5 blur-3xl pointer-events-none" aria-hidden />
            <div className="absolute -bottom-24 right-0 w-96 h-96 rounded-full bg-brand-yellow/10 blur-3xl pointer-events-none" aria-hidden />

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="md:flex md:items-end md:justify-between gap-8 mb-10">
                    <div className="max-w-3xl">
                        <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-4">
                            Customer Testimonials
                        </div>
                        <h2 className="font-display text-4xl sm:text-5xl font-black text-slate-900 tracking-tighter leading-tight">
                            Words from the people who ship with us
                            <span className="text-brand-yellow">.</span>
                        </h2>
                        <p className="mt-5 text-slate-600 leading-relaxed">
                            Real feedback from customers we&apos;ve been running lanes,
                            warehouses and inbound programmes for — over multiple years.
                        </p>
                    </div>
                    <div className="hidden md:flex items-center gap-3 shrink-0 pb-2">
                        <button
                            type="button"
                            aria-label="Previous testimonials"
                            data-testid="testimonials-prev-btn"
                            onClick={() => goTo(page - 1)}
                            className="w-11 h-11 rounded-full border border-slate-300 text-slate-700 flex items-center justify-center hover:bg-slate-900 hover:text-white hover:border-slate-900 transition-colors"
                        >
                            <ChevronLeft className="w-5 h-5" />
                        </button>
                        <button
                            type="button"
                            aria-label="Next testimonials"
                            data-testid="testimonials-next-btn"
                            onClick={() => goTo(page + 1)}
                            className="w-11 h-11 rounded-full border border-slate-300 text-slate-700 flex items-center justify-center hover:bg-slate-900 hover:text-white hover:border-slate-900 transition-colors"
                        >
                            <ChevronRight className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Carousel track */}
                <div
                    className="overflow-hidden -my-4 py-4"
                    data-testid="testimonials-carousel"
                    onMouseEnter={() => setPaused(true)}
                    onMouseLeave={() => setPaused(false)}
                    onTouchStart={() => setPaused(true)}
                    onTouchEnd={() => setPaused(false)}
                >
                    <motion.div
                        className="flex items-stretch"
                        animate={{ x: `-${page * 100}%` }}
                        transition={{ type: "spring", stiffness: 120, damping: 22 }}
                    >
                        {testimonials.map((t, i) => (
                            <div key={`${t.company}-${i}`} className="w-full md:w-1/2 shrink-0 px-4 pt-4">
                                <TestimonialCard t={t} i={i} />
                            </div>
                        ))}
                    </motion.div>
                </div>

                {/* Dots + mobile arrows */}
                <div className="mt-10 flex items-center justify-center gap-3">
                    <button
                        type="button"
                        aria-label="Previous testimonials"
                        data-testid="testimonials-prev-btn-mobile"
                        onClick={() => goTo(page - 1)}
                        className="md:hidden w-10 h-10 rounded-full border border-slate-300 text-slate-700 flex items-center justify-center"
                    >
                        <ChevronLeft className="w-4 h-4" />
                    </button>
                    <div className="flex items-center gap-2" data-testid="testimonials-dots">
                        {Array.from({ length: pageCount }).map((_, p) => (
                            <button
                                key={p}
                                type="button"
                                aria-label={`Go to testimonials page ${p + 1}`}
                                data-testid={`testimonials-dot-${p}`}
                                onClick={() => goTo(p)}
                                className={`h-2 rounded-full transition-all duration-300 ${
                                    p === page ? "w-8 bg-blue-600" : "w-2 bg-slate-300 hover:bg-slate-400"
                                }`}
                            />
                        ))}
                    </div>
                    <button
                        type="button"
                        aria-label="Next testimonials"
                        data-testid="testimonials-next-btn-mobile"
                        onClick={() => goTo(page + 1)}
                        className="md:hidden w-10 h-10 rounded-full border border-slate-300 text-slate-700 flex items-center justify-center"
                    >
                        <ChevronRight className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </section>
    );
};

export default TestimonialsSection;
